import React from 'react';
import autoBind from 'react-autobind';
import { Route, Switch, Redirect } from 'react-router-dom';
import CommunicationLocationOff from 'material-ui/svg-icons/communication/location-off';
import { appContainer, Subscribe } from '../../contexts/app';
import { MarkerTypeRecord } from '../../feathers/records';
import { PanelContainer, Panel } from '../../components/Panel';
import noty from '../../lib/noty';
import FieldImage from '../../components/FieldImage';
import FieldSelect from '../../components/FieldSelect';
import Field from '../../components/Field';
import HelpText from '../../components/HelpText';
import { MarkerCategoryPanel } from '../../components/MarkerCategoryPanel';
import SidebarPage from '../../components/Sidebar/components/SidebarPage';
import SidebarMarkerTypes from '../../components/Sidebar/SidebarMarkerTypes';
import './style.scss';

class MarkerTypes extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      markerTypes: this.props.markerTypes,
      markerTypeCategories: this.props.markerTypeCategories,
      categoryOptions: this.props.markerTypeCategories
        .toList()
        .map(category => ({ value: category._id, text: category.name }))
    };
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      markerTypes: nextProps.markerTypes,
      markerTypeCategories: nextProps.markerTypeCategories
    });
  }

  handleRedirect(props) {
    // We need to ensure id's exist at each path endpoint
    // If not, this is either an invalid id or it has been deleted
    const { path, params } = props.match;
    let redirect = null;
    if (
      path === '/marker-types/:markerTypeId' &&
      !props.markerTypes.has(params.markerTypeId)
    ) {
      redirect = `/marker-types`;
    }
    return redirect;
  }

  render() {
    const redirect = this.handleRedirect(this.props);
    if (redirect) {
      return <Redirect to={redirect} />;
    }
    const { facilities } = this.props;
    const facility = this.props.location.query.facilityId
      ? facilities.get(this.props.location.query.facilityId)
      : facilities.first();
    const facilityId = facility._id;
    const defaultCenter = facility.geo.coordinates;
    const { markerTypeId } = this.props.match.params;
    const { markerTypes, markerTypeCategories, categoryOptions } = this.state;
    const markerType = markerTypes.get(markerTypeId);
    return (
      <div id="markerTypesPage" className="authed-page">
        <SidebarMarkerTypes
          markerTypes={markerTypes.toList().toJS()}
          categories={markerTypeCategories.toList().toJS()}
          markerType={markerType}
          facilityId={facilityId}
          match={this.props.match}
        />
        <SidebarPage>
          <Switch>
            <Route
              exact
              path="/marker-types"
              render={routeProps => {
                const markerType = new MarkerTypeRecord();
                return (
                  <MarkerTypeForm
                    {...routeProps}
                    markerType={markerType}
                    categoryOptions={categoryOptions}
                    markerTypeCategories={markerTypeCategories}
                    markerTypes={markerTypes}
                    defaultCenter={defaultCenter}
                  />
                );
              }}
            />
            <Route
              exact
              path="/marker-types/:markerTypeId"
              render={routeProps => {
                const markerType = markerTypes.get(markerTypeId);
                return (
                  <MarkerTypeForm
                    {...routeProps}
                    markerType={markerType}
                    categoryOptions={categoryOptions}
                    markerTypeCategories={markerTypeCategories}
                    defaultCenter={defaultCenter}
                    markerTypes={markerTypes}
                    key={markerType._id}
                  />
                );
              }}
            />
          </Switch>
        </SidebarPage>
      </div>
    );
  }
}

export default routeProps => {
  return (
    <Subscribe
      to={[appContainer]}
      render={app => {
        return (
          <MarkerTypes
            {...routeProps}
            markerTypes={app.state.get('markerTypes')}
            markerTypeCategories={app.state.get('markerTypeCategories')}
            facilities={app.state.get('facilities')}
          />
        );
      }}
    />
  );
};

class MarkerTypeForm extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      panelOpen: true,
      fetching: false,
      touched: false,
      markerType: this.props.markerType
    };
  }

  handleChange({ name, value }) {
    const path = name.split('.');
    this.setState({
      touched: true,
      markerType: this.state.markerType.setIn(path, value)
    });
  }

  handleSubmit() {
    const { markerType } = this.state;
    const method = markerType._id ? 'patch' : 'create';
    this.setState({ fetching: true });
    markerType[method]()
      .then(updated => {
        this.setState({ fetching: false, touched: false });
        noty.success(
          markerType._id ? 'Marker Type Updated' : 'Marker Type Created'
        );
      })
      .catch(err => {
        this.setState({ fetching: false });
        noty.errors(err);
      });
  }

  handlePanelToggle() {
    this.setState({ panelOpen: !this.state.panelOpen });
  }

  handleRemove() {
    const { markerType } = this.state;
    noty.confirm(`Are you sure you want to delete "${markerType.name}"`, {
      onOk: () => {
        this.setState({ fetching: true });
        markerType
          .remove()
          .then(removed => {
            noty.success('Marker Type removed.');
          })
          .catch(err => {
            this.setState({ fetching: false });
            noty.errors(err);
          });
      }
    });
  }

  render() {
    const { markerType, fetching, touched } = this.state;
    const { categoryOptions, markerTypeCategories, markerTypes } = this.props;
    return (
      <div id="markerTypeForm">
        <PanelContainer
          width={475}
          open={this.state.panelOpen}
          onToggle={this.handlePanelToggle}
        >
          <Panel
            checkCanAdd="marker-types"
            onSaveClick={this.handleSubmit}
            saveTitle={
              markerType._id ? 'Save Marker Type' : 'Create Marker Type'
            }
            onDeleteClick={markerType._id && this.handleRemove}
            fetching={fetching}
            touched={touched}
            touchedMessage="Are you sure that you want to leave? This building has unsaved changes."
          >
            <div id="icon">
              <Field
                name="name"
                value={markerType.name}
                onChange={this.handleChange}
                label="Marker Type Name"
                disabled={fetching}
              />
              <IconPreview name={markerType.name} src={markerType.image.path} />
            </div>
            <FieldImage
              value={markerType.image}
              name="image"
              onChange={this.handleChange}
              label="Marker Type Icon"
              disabled={fetching}
            />
            <FieldSelect
              name="category_id"
              value={markerType.category_id}
              onChange={this.handleChange}
              label="Marker Type Category"
              options={categoryOptions}
              disabled={fetching}
            />
            <p className="text-muted">
              Marker images are best uploaded at 40px x 40px. Upload a new image
              and view the preview of your marker above.
            </p>
            <HelpText
              href="https://mapplug.zendesk.com/hc/en-us/articles/360008924193-Creating-custom-marker-types"
              description="Need help? See our guide on creating a custom marker type."
            />
          </Panel>
          <MarkerCategoryPanel
            markerTypes={markerTypes}
            markerTypeCategories={markerTypeCategories}
            disabled={fetching}
          />
        </PanelContainer>
      </div>
    );
  }
}

const IconPreview = props => {
  const image = props.src ? (
    <img alt={props.name} src={props.src} />
  ) : (
    <React.Fragment>
      <CommunicationLocationOff color="disabled" />
      <p className="text-muted">No image chosen</p>
    </React.Fragment>
  );

  const iconPreview = (
    <div id="iconPreview">
      <label className="field-label"> Icon Preview </label>
      {image}
    </div>
  );
  return iconPreview;
};



// WEBPACK FOOTER //
// ./src/pages/MarkerTypes/index.jsx